import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
  items: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 1,
  },
  lastParams: null,
  query: { search: "", limit: 12, page: 1 },
  status: "idle",
  error: null,
  success: null,
  loading: false,
};

export const fetchCategories = createAsyncThunk(
  "category/getAll",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/category/all`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

// NOTE: `/category/all` (getAllCategory) is the plain, unfiltered admin
// category listing (includes categories with no products yet, unlike
// `/category` root which is a storefront-shaped, product-populated endpoint
// only meant for the public menu). It now supports opt-in page/limit/search
// pagination, same as `/product` and `/type`. The fulfilled reducer below
// still detects a bare-array response and falls back to client-side search +
// pagination, purely as a defensive fallback if the endpoint is ever called
// without pagination params.
export const fetchCategoriesPaginated = createAsyncThunk(
  "category/getAllPaginated",
  async ({ restaurantId, search, limit, page }) => {
    try {
      const response = await axios.get(`${apiUrl}/category/all`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          search,
          limit,
          page,
        },
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addCategory = createAsyncThunk(
  "category/addCategory",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/category`,
        body,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const modifyCategory = createAsyncThunk(
  "category/modifyCategory",
  async ({ body, categoryId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/category/update/${categoryId}`,
        body,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const updatePositions = createAsyncThunk(
  "category/updatePositions",
  async ({ positions, categoryId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/category/position/${categoryId}`,
        { positions },
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const updateCategoryPositions = createAsyncThunk(
  "category/updateCategoryPositions",
  async ({ positions, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/category/position`,
        { positions },
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "category/deleteCategory",
  async ({ categoryId, restaurantId }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/category/${categoryId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
    setCategoriesQuery: (state, action) => {
      state.query = { ...state.query, ...action.payload };
    },
    resetCategoriesQuery: (state) => {
      state.query = { search: "", limit: 12, page: 1 };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCategories.pending, (state, action) => {
        state.status = "loadingFetchData";
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCategoriesPaginated.pending, (state, action) => {
        state.status = "loadingFetchDataPaginated";
        state.loading = true;
      })
      .addCase(fetchCategoriesPaginated.fulfilled, (state, action) => {
        state.status = "fetchDataPaginated";
        state.loading = false;
        const payload = action.payload;
        if (Array.isArray(payload)) {
          // Backend hasn't added opt-in pagination/search to /category/all
          // yet — fall back to client-side search + slicing.
          const { search = "", limit = 10, page = 1 } = action.meta?.arg || {};
          const term = String(search || "").trim().toLowerCase();
          const filtered = term
            ? payload.filter((category) =>
                (category.name || "").toLowerCase().includes(term)
              )
            : payload;
          const safeLimit = Number(limit) || 10;
          const safePage = Number(page) || 1;
          const start = (safePage - 1) * safeLimit;
          state.items = filtered.slice(start, start + safeLimit);
          state.pagination = {
            currentPage: safePage,
            totalPages: Math.max(1, Math.ceil(filtered.length / safeLimit)),
            totalRecords: filtered.length,
          };
        } else {
          state.items = payload.categories || [];
          state.pagination = payload.pagination || state.pagination;
        }
        state.lastParams = JSON.stringify(action.meta.arg);
      })
      .addCase(fetchCategoriesPaginated.rejected, (state, action) => {
        state.status = "fetchErrorPaginated";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addCategory.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addCategory.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
        if (action.payload?.category?._id) {
          const exists = state.items.some(
            (item) => item._id === action.payload.category._id
          );
          if (!exists) {
            state.items.unshift(action.payload.category);
            if (state.pagination) {
              state.pagination.totalRecords =
                (state.pagination.totalRecords || 0) + 1;
            }
          }
        }
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteCategory.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;
        const deletedId = action.meta?.arg?.categoryId;
        if (deletedId) {
          state.items = state.items.filter((item) => item._id !== deletedId);
          if (state.pagination) {
            state.pagination.totalRecords = Math.max(
              0,
              (state.pagination.totalRecords || 0) - 1
            );
          }
        }
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(modifyCategory.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(modifyCategory.fulfilled, (state, action) => {
        state.status = "modifySuccess";
        state.loading = false;
        state.success = action.payload.message;
        const updatedCategory = action.payload?.category;
        if (updatedCategory && updatedCategory._id) {
          const categoryIndex = state.items.findIndex(
            (item) => item._id === updatedCategory._id
          );
          if (categoryIndex !== -1) {
            state.items[categoryIndex] = updatedCategory;
          }
        }
      })
      .addCase(modifyCategory.rejected, (state, action) => {
        state.status = "modifyError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updatePositions.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(updatePositions.fulfilled, (state, action) => {
        state.status = "updatePositionSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(updatePositions.rejected, (state, action) => {
        state.status = "updatePositionError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateCategoryPositions.pending, (state, action) => {
        state.status = "loading1";
        state.loading = true;
      })
      .addCase(updateCategoryPositions.fulfilled, (state, action) => {
        state.status = "updateCategoryPositionSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(updateCategoryPositions.rejected, (state, action) => {
        state.status = "updateCategoryPositionError";
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateStatus, setCategoriesQuery, resetCategoriesQuery } =
  categoriesSlice.actions;
export const selectAllCategories = (state) => state.categories.items;
export const selectCategoriesPagination = (state) =>
  state.categories.pagination;
export const selectCategoriesLastParams = (state) =>
  state.categories.lastParams;
export const selectCategoriesQuery = (state) => state.categories.query;
export const getCategoriesStatus = (state) => state.categories.status;
export const getCategoriesError = (state) => state.categories.error;
export const getCategoriesSuccess = (state) => state.categories.success;
export const getCategoriesLoading = (state) => state.categories.loading;
export default categoriesSlice.reducer;
